<template>
    <v-app>
        <md-snackbar :md-position="snackbar.position"
            :md-duration="snackbar.duration"
            :md-active.sync="snackbar.visibleSnackbar"
            :md-persistent="false">
            <span>{{ snackbar.alertMessage }}</span>
            <md-button class="md-primary"
                @click="snackbar.visibleSnackbar = false">
                {{ $t('actions.dismiss') }}
            </md-button>
        </md-snackbar>

        <md-toolbar>
            <!--Left search -->
            <div class="md-toolbar-section-start table-toolbar-left-section">
                <md-field md-clearable>
                    <md-input :placeholder="$t('general.search-by-name')"
                        v-model="search"
                        @input="fetchStatistics">
                    </md-input>
                </md-field>
            </div>
            <!--Right actions -->
            <div class="md-toolbar-section-end">
                <md-field>
                    <flat-pickr v-model="startDate"
                        :config="startDateConfig"
                        class="form-control"
                        :placeholder="$t('general.start-date')"
                        @input="fetchStatistics"
                        @on-change="onStartDateChange">
                    </flat-pickr>
                </md-field>
                <md-field>
                    <flat-pickr v-model="endDate"
                        :config="endDateConfig"
                        class="form-control"
                        :placeholder="$t('general.end-date')"
                        @input="fetchStatistics"
                        @on-change="onEndDateChange">
                    </flat-pickr>
                </md-field>
                <md-button class="md-icon-button"
                    @click="fetchStatistics">
                    <md-icon>refresh</md-icon>
                </md-button>
                <md-button class="md-icon-button"
                    @click="showStatisticsPreview"
                    :disabled="!selected.length">
                    <md-icon>print</md-icon>
                    <md-tooltip md-direction="top">
                        {{ $t('general.print') }}
                    </md-tooltip>
                </md-button>
            </div>
        </md-toolbar>

        <div>
            <md-progress-spinner v-if="isLoading"
                md-mode="indeterminate"></md-progress-spinner>

            <v-data-table v-else
                v-model="selected"
                :headers="headers"
                :items="dataSource"
                :pagination.sync="pagination"
                select-all
                item-key="row_id"
                class="elevation-1"
                rows-per-page-text="$vuetify.dataIterator.rowsPerPageText"
                no-results-text="$vuetify.noDataText"
                no-data-text="$vuetify.noDataText"
                :rows-per-page-items="paginationAux.rowsPerPageItems"
                :total-items="paginationAux.totalItems"
                expand>
                <template v-slot:items="props">
                    <tr>
                        <td>
                            <v-checkbox v-model="props.selected"
                                primary
                                hide-details></v-checkbox>
                        </td>
                        <td class="text-xs-right">
                            {{ props.item.total }}
                        </td>
                        <td class="text-xs-left">
                            {{ props.item.unit_name }}
                        </td>
                        <td class="text-xs-left">
                            {{ props.item.product_name }}
                        </td>
                        <td class="text-xs-left">
                            {{ props.item.caliber_name }}
                        </td>
                        <td class="text-xs-left">
                            {{ props.item.state_name }}
                        </td>
                        <td class="justify-center px-0">
                            <md-button class="md-icon-button"
                                @click="props.expanded = !props.expanded">
                                <md-icon>remove_red_eye</md-icon>
                                <md-tooltip md-direction="top">
                                    {{ $t('actions.view') }}
                                </md-tooltip>
                            </md-button>
                        </td>
                    </tr>
                </template>
                <!-- EXPANDED TABLE  -->
                <template slot="expand"
                    slot-scope="props">
                    <md-table>
                        <md-table-row>
                            <md-table-head class="text-center">
                                {{ $tc('orders.order',1) }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $tc('organizations.organization',1) }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $t('orders.delivery-date') }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $t('orders.order-status') }}
                            </md-table-head>
                            <md-table-head class="text-center">
                                {{ $tc('products.quantity',1) }}
                            </md-table-head>
                        </md-table-row>
                        <md-table-row v-for="(order,index) in props.item.orders"
                            :key="index">
                            <td>{{ order.order_id }}</td>
                            <td>{{ order.organization_name }}</td>
                            <td>{{ formatDateTime(order.delivery_date) }}</td>
                            <td>{{ order.description }}</td>
                            <td>{{ order.quantity }}</td>
                        </md-table-row>
                    </md-table>
                </template>
            </v-data-table>
        </div>
    </v-app>
</template>

<script>
import ordersStatusService from '@/components/orders/services/ordersStatus'
import reportsService from '@/reports/services/'
import _ from 'lodash'
import FlatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import luxon from '@/mixins/luxon'

const loadingKeys = {
    fetchingItem: 'fetching-statistics'
}

export default {
    name: 'Statistics',

    mixins: [luxon],

    components: {
        FlatPickr
    },

    data () {
        return {
            loadings: [],

            ordersStatus: [],

            startDate: this.initStartDate(),
            endDate: this.initEndDate(),

            dataSource: [],
            ordersDataSource: [],

            selected: [],

            expanded: [],
            singleExpand: true,

            filter: '0',

            search: '',

            startDateConfig: {
                altFormat: 'F j, Y',
                enableTime: true,
                noCalendar: false,
                dateFormat: 'Y-m-d H:i',
                locale: Portuguese,
                maxDate: this.endDate
            },

            endDateConfig: {
                altFormat: 'F j, Y',
                enableTime: true,
                noCalendar: false,
                dateFormat: 'Y-m-d H:i',
                locale: Portuguese,
                minDate: this.startDate
            },

            snackbar: {
                visibleSnackbar: false,
                position: 'center',
                duration: 3000,
                alertMessage: this.$t('validations.operation-performed-successfully')
            },

            paginationAux: {
                descending: true,
                page: 1,
                rowsPerPage: 10,
                sortBy: 'order_id',
                totalItems: 0,
                rowsPerPageItems: [5, 10, 15, 25, 50]
            },

            headers: [
                {
                    text: this.$t('general.total'),
                    align: 'right',
                    value: 'total',
                    sortable: false
                },
                {
                    text: this.$tc('units.unit', 1),
                    align: 'left',
                    value: 'unit_name',
                    sortable: false
                },
                {
                    text: this.$t('products.product-name'),
                    align: 'left',
                    value: 'product_name',
                    sortable: true
                },
                {
                    text: this.$tc('calibers.caliber', 1),
                    align: 'left',
                    value: 'caliber_name',
                    sortable: false
                },
                {
                    text: this.$t('orders.fish-status'),
                    align: 'left',
                    value: 'state_name',
                    sortable: false
                },
                {
                    text: this.$tc('general.action', 2),
                    align: 'center',
                    value: '',
                    sortable: false
                }
            ]
        }
    },

    computed: {
        isLoading () {
            return this.isFetchingStatistics
        },

        isFetchingStatistics () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        },

        pagination: {
            get: function () {
                return this.paginationAux
            },

            set: function (value) {
                this.paginationAux = value
                this.fetchStatistics()
            }
        }
    },

    methods: {
        async fetchOrderStatus () {
            let self = this

            let params = {
                filter: {
                    active: 1
                }
            }

            let response = await ordersStatusService.getOrdersStatus(params)
            self.ordersStatus = _.cloneDeep(response.data.results)
        },

        async fetchStatistics () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            self.dataSource = []
            let parameters = {
                pagination: {
                    page: self.paginationAux.page,
                    rowsPerPage: self.paginationAux.rowsPerPage
                },
                filter: {
                    startDate: new Date(self.startDate).toISOString(),
                    endDate: new Date(self.endDate).toISOString(),
                    product_name: self.search
                }
            }

            let response = await reportsService.retrieve(parameters)

            self.dataSource = _.cloneDeep(response.data.results)

            self.paginationAux.totalItems = response.data.pagination.totalItems
            self.paginationAux.rowsPerPage = response.data.pagination.rowsPerPage
            self.paginationAux.currentPage = response.data.pagination.currentPage

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async showStatisticsPreview () {
            this.$store.dispatch('printPreview', this.selected)
            this.$router.push('/reports/preview')
        },

        initStartDate () {
            let startDate = new Date()
            startDate.setHours(0)
            startDate.setMinutes(0)

            return startDate
        },

        initEndDate () {
            let endDate = new Date()
            endDate.setHours(23)
            endDate.setMinutes(59)

            return endDate
        },

        onStartDateChange (selectedDates, dateStr, instance) {
            this.$set(this.endDateConfig, 'minDate', dateStr)
        },

        onEndDateChange (selectedDates, dateStr, instance) {
            this.$set(this.startDateConfig, 'maxDate', dateStr)
        }
    },

    async created () {
        await this.fetchOrderStatus()
    }
}
</script>
